import { i18n } from '../i18n.js';
/**
 * Account menu for public user/profile affordances.
 * Provides direct legal actions without depending on hidden DOM buttons.
 */


import { getAccountActions } from '../app-services.js';
import { GdprSettings } from './gdpr-settings.js';
import { WithdrawalPanel } from './withdrawal-panel.js';
import { Platform } from './platform.js';
import { uiCssPx, uiPx } from './ui-scale.js';
let AccountMenu = {};

AccountMenu = (function () {
  const MENU_ID = "account-ctx-menu";

  function uiText(key, fallback, replacements = {}) {
    const translated = i18n?.t?.(key, replacements);
    return translated && translated !== key ? translated : fallback;
  }

  function applyItemStyles(node, color) {
    node.style.cssText = `
      padding: ${uiCssPx(8)} ${uiCssPx(12)};
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: ${uiCssPx(8)};
      color: ${color};
      border-radius: ${uiCssPx(4)};
      background: transparent;
      border: 0;
      width: 100%;
      text-align: left;
      font: inherit;
      text-decoration: none;
      box-sizing: border-box;
    `;
    node.addEventListener("mouseenter", () => {
      node.style.background = "#3a3a3a";
    });
    node.addEventListener("mouseleave", () => {
      node.style.background = "transparent";
    });
  }

  function createLabel(text) {
    const label = document.createElement("strong");
    label.textContent = text;
    return label;
  }

  function createActionItem(text, color, action, closeMenu) {
    const button = document.createElement("button");
    button.type = "button";
    button.dataset.accountMenuAction = action;
    applyItemStyles(button, color);
    button.appendChild(createLabel(text));
    button.addEventListener("click", (event) => {
      event.stopPropagation();
      closeMenu();
      if (action === "redeem") {
        // THE ONE PLACE A CODE IS TYPED. The dialog is published by main.js
        // like Sign Out, so this leaf never imports the app.
        getAccountActions()?.redeemVoucher?.();
        return;
      }
      if (action === "signout") {
        // This dropdown is the only place Sign Out is offered. The action
        // itself (UI.Firebase.signOut plus its UI cleanup) is published by
        // main.js so the auth path stays in one place.
        getAccountActions()?.signOut?.();
        return;
      }
      if (action === "privacy") {
        GdprSettings?.showPrivacySettings?.();
        return;
      }
      if (action === "withdraw") {
        void WithdrawalPanel?.open?.();
        return;
      }
      if (action === "export") {
        GdprSettings?.exportData?.();
        return;
      }
      if (action === "delete") {
        GdprSettings?.showDeleteConfirmation?.();
      }
    });
    return button;
  }

  function createLinkItem(text, color, href, closeMenu) {
    const link = document.createElement("a");
    link.href = href;
    link.target = "_blank";
    link.rel = "noopener";
    applyItemStyles(link, color);
    link.appendChild(createLabel(text));
    link.addEventListener("click", (event) => {
      event.stopPropagation();
      closeMenu();
    });
    return link;
  }

  function removeExistingMenu() {
    const existing = document.getElementById(MENU_ID);
    if (existing) {
      existing.remove();
    }
  }

  function updateInteractionTitle(element) {
    const mobileUi = !!Platform?.isMobileUi?.();
    element.title = mobileUi
      ? uiText("account.manageTitleTap", "Tap to manage account")
      : uiText("account.manageTitle", "Click to manage account");
  }

  function createMenu(clickEvent, triggerElement) {
    removeExistingMenu();

    const menu = document.createElement("div");
    menu.id = MENU_ID;
    menu.style.cssText = `
      position: fixed;
      top: ${clickEvent.clientY + uiPx(10)}px;
      left: ${clickEvent.clientX}px;
      background: #2a2a2a;
      border: ${uiCssPx(1)} solid #555;
      padding: ${uiCssPx(6)};
      border-radius: ${uiCssPx(8)};
      z-index: 10000;
      box-shadow: 0 ${uiCssPx(4)} ${uiCssPx(12)} rgba(0,0,0,0.5);
      font-family: sans-serif;
      font-size: ${uiCssPx(14)};
      min-width: ${uiCssPx(220)};
    `;

    const closeMenu = () => {
      if (menu.isConnected) {
        menu.remove();
      }
    };

    // Sign Out lives here so the mobile lobby (where the top-bar button may be
    // compacted away) still has a reachable sign-out. Reuses auth.signOut ×8.
    menu.appendChild(createActionItem(uiText("voucher.menuItem", "Redeem a code"), "#e7ca82", "redeem", closeMenu));
    menu.appendChild(createActionItem(uiText("auth.signOut", "Sign Out"), "#f0d6a0", "signout", closeMenu));
    // The optional consents (analytics, email news) are withdrawn here; the
    // Privacy Policy names this panel as the place.
    menu.appendChild(createActionItem(uiText("gdpr.title", "Privacy & Data"), "#b99adc", "privacy", closeMenu));
    // The statutory withdrawal function (Consumer Rights Directive Art. 11a):
    // a purchase can be withdrawn from here for as long as the right lasts.
    menu.appendChild(createActionItem(uiText("withdrawal.menuItem", "Withdraw from a purchase"), "#b99adc", "withdraw", closeMenu));
    menu.appendChild(createActionItem(uiText("gdpr.downloadLabel", "Download My Data"), "#b99adc", "export", closeMenu));
    menu.appendChild(createActionItem(uiText("gdpr.deleteMenuLabel", "Delete Account"), "#ff6b6b", "delete", closeMenu));
    menu.appendChild(createLinkItem(uiText("gdpr.terms", "Terms of Service"), "#d7d7d7", "/terms", closeMenu));
    menu.appendChild(createLinkItem(uiText("gdpr.privacy", "Privacy Policy"), "#d7d7d7", "/privacy", closeMenu));
    menu.appendChild(createLinkItem(uiText("legal.dmca", "Copyright"), "#d7d7d7", "/dmca", closeMenu));

    document.body.appendChild(menu);

    // Clamp into the viewport: the menu opens at the raw tap X and the username
    // trigger sits far right, so a 220px menu would shoot off a phone's right
    // edge (and off the bottom in landscape). No-op when it already fits.
    const rect = menu.getBoundingClientRect();
    const pad = uiPx(8);
    let left = clickEvent.clientX;
    let top = clickEvent.clientY + uiPx(10);
    if (left + rect.width + pad > window.innerWidth) left = window.innerWidth - rect.width - pad;
    if (top + rect.height + pad > window.innerHeight) top = window.innerHeight - rect.height - pad;
    menu.style.left = `${Math.max(pad, left)}px`;
    menu.style.top = `${Math.max(pad, top)}px`;

    setTimeout(() => {
      const cleanup = (event) => {
        const target = event.target;
        if (target instanceof Node && (menu.contains(target) || triggerElement.contains(target))) {
          return;
        }
        closeMenu();
        window.removeEventListener("click", cleanup);
      };
      window.addEventListener("click", cleanup);
    }, 10);

    return menu;
  }

  function attach(element) {
    if (!element) return;
    if (element.dataset.accountMenuBound === "true") return;
    updateInteractionTitle(element);
    element.style.cursor = "pointer";
    element.dataset.accountMenuBound = "true";

    const refreshInteractionTitle = () => updateInteractionTitle(element);
    window.addEventListener("moonfall:viewport-change", refreshInteractionTitle);
    window.addEventListener("moonfall:languagechange", refreshInteractionTitle);
    window.addEventListener("moonfall:i18nready", refreshInteractionTitle);

    const openMenu = (event) => {
      event.preventDefault();
      event.stopPropagation();
      createMenu(event, element);
    };

    element.addEventListener("contextmenu", openMenu);
    element.addEventListener("click", openMenu);
    element.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      openMenu(event);
    });
  }

  return {
    attach,
  };
})();

export { AccountMenu };
